import { ContentContext, LocaleContext } from 'Components/hooks/context';
import React, { useContext, useState } from 'react';
import { faCaretDown, faCaretUp, faSearch } from '@fortawesome/free-solid-svg-icons';

import AtlasMetadataClient from '@reuters-graphics/graphics-atlas-client';
import Autosuggest from 'react-autosuggest';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import { component } from './styles.module.scss';
import { getCountryOverallTrend } from 'Utils/calcOverallTrend';
import { useRouter } from 'next/router';

const atlas = new AtlasMetadataClient();

const Search = ({ countriesWithStats, globalStatistics }) => {
  const locale = useContext(LocaleContext);
  const content = useContext(ContentContext).common.linkbox;

  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isRouting, setIsRouting] = useState(false);
  const router = useRouter();

  const countries = atlas.countries
    .filter(c => countriesWithStats.includes(c.isoAlpha2));

  const countryOptions = countries
    .map((country) => {
      return {
        label: country.translations[locale],
        slug: country.slug,
      };
    });

  countryOptions.sort((a, b) => a.label > b.label ? 1 : -1);

  const renderInput = (props) => (
    <div className='searchBox'>
      {isRouting ? (
        <div className='ring-loader'><div /><div /><div /><div /></div>
      ) : (
        <FontAwesomeIcon className='search-icon' icon={faSearch} fixedWidth />
      )}
      <input {...props} />
    </div>
  );

  const renderSuggestion = (suggestion, { query, isHighlighted }) => {
    const country = atlas.getCountry(suggestion.slug);
    const distributionRank = globalStatistics.distributionRanks.cases[country.isoAlpha2];
    const trend = getCountryOverallTrend(globalStatistics, country.isoAlpha2);
    return (
      <div className={classnames('item', { highlighted: isHighlighted, peak: distributionRank >= 0.9 })}>
        {suggestion.label}
        {(trend.cases && trend.cases.increasing) && (
          <FontAwesomeIcon icon={faCaretUp} />
        )}
        {(trend.cases && trend.cases.decreasing) && (
          <FontAwesomeIcon icon={faCaretDown} />
        )}
      </div>
    );
  };

  const onChange = (event, { newValue }) => {
    setSearch(newValue);
  };

  const onBlur = (event, { highlightedSuggestion }) => {
    if (!highlightedSuggestion) return;
    setSearch(highlightedSuggestion.label);
    navigate(highlightedSuggestion.slug);
  };

  const inputProps = {
    placeholder: content.search.placeholder,
    value: search,
    onChange,
    onBlur,
  };

  const getSuggestions = value => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;

    return inputLength === 0 ? [] : countries
      .filter(c => c.translations[locale].toLowerCase().includes(value.toLowerCase()))
      .map((country) => {
        return {
          label: country.translations[locale],
          slug: country.slug,
        };
      });
  };

  const getSuggestionValue = suggestion => suggestion.label;

  const onSuggestionsFetchRequested = ({ value }) => setSuggestions(getSuggestions(value));

  const onSuggestionsClearRequested = () => setSuggestions([]);

  const onNavigate = () => {
    setSearch('');
    setIsRouting(false);
    window.scrollTo(0, 0);
    document.body.focus();
  };

  const navigate = (slug) => {
    setIsRouting(true);
    if (locale === 'en') {
      router.push('/countries-and-territories/[country]', `/countries-and-territories/${slug}`)
        .then(onNavigate);
    } else {
      router.push('/[locale]/countries-and-territories/[country]', `/${locale}/countries-and-territories/${slug}`)
        .then(onNavigate);
    }
  };

  const onSuggestionSelected = (e, { suggestion }) => navigate(suggestion.slug);

  return (
    <div className={classnames(component)}>
      <Autosuggest
        suggestions={suggestions}
        renderInputComponent={renderInput}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        inputProps={inputProps}
        onSuggestionSelected={onSuggestionSelected}
        highlightFirstSuggestion={false}
        focusInputOnSuggestionClick={false}
      />
    </div>
  );
};

export default Search;
